import { AICourse } from './types';
import { 
  MODULE_1_AI_INTRODUCTION,
  AI_FUNDAMENTALS_MODULE_2,
  AI_FUNDAMENTALS_MODULE_3,
  AI_FUNDAMENTALS_MODULE_4
} from './modules/ai-fundamentals';
import { 
  FINANCIAL_AI_MODULE_1_FOUNDATIONS,
  FINANCIAL_AI_MODULE_2_ALGORITHMIC_TRADING,
  FINANCIAL_AI_MODULE_3_RISK_MANAGEMENT
} from './modules/financial-ai';
import { 
  HEALTHCARE_AI_MODULE_1_FOUNDATIONS,
  HEALTHCARE_AI_MODULE_2_MEDICAL_IMAGING,
  HEALTHCARE_AI_MODULE_3
} from './modules/healthcare-ai';
import { 
  GAMING_AI_MODULE_1,
  GAMING_AI_MODULE_2,
  GAMING_AI_MODULE_3,
  GAMING_AI_MODULE_4,
  GAMING_AI_MODULE_5,
  GAMING_AI_MODULE_6
} from './modules/gaming-ai';
import { 
  AUTONOMOUS_SYSTEMS_MODULE_1,
  AUTONOMOUS_SYSTEMS_MODULE_2,
  AUTONOMOUS_SYSTEMS_MODULE_3
} from './modules/autonomous-systems';
import { 
  MLOPS_MODULE_1,
  MLOPS_MODULE_2
} from './modules/mlops';

import { 
  ENHANCED_AI_FUNDAMENTALS_MODULE_1, 
  ENHANCED_AI_FUNDAMENTALS_MODULE_2,
  ENHANCED_AI_FUNDAMENTALS_MODULE_3
} from './modules/enhanced-fundamentals';
import { RAG_SUPABASE_MODULE_1 } from './modules/rag-supabase';

// Import new advanced AI course modules
import {
  AI_AGENT_INTRO_MODULE,
  LANGCHAIN_ORCHESTRATION_MODULE,
  MULTI_AGENT_SYSTEMS_MODULE,
  TOOL_USING_AGENTS_MODULE,
  AGENT_MEMORY_MODULE,
  CONVERSATIONAL_AI_MODULE,
  ADVANCED_AGENT_PATTERNS_MODULE
} from './modules/ai-agent-development';

import {
  STREAMLIT_AI_MODULE,
  GRADIO_ML_MODULE,
  HUGGINGFACE_INTEGRATION_MODULE,
  NOCODE_AI_TOOLS_MODULE,
  AI_API_INTEGRATION_MODULE,
  VOICE_AI_DEVELOPMENT_MODULE
} from './modules/ai-prototyping';

import {
  CUSTOM_LLM_INTEGRATION_MODULE,
  FUNCTION_CALLING_TOOLS_MODULE,
  ADVANCED_PROMPT_ENGINEERING_MODULE,
  LLM_EVALUATION_MODULE,
  ENTERPRISE_LLM_DEPLOYMENT_MODULE
} from './modules/advanced-llm-integration';

import {
  MODERN_COMPUTER_VISION_MODULE,
  REALTIME_OBJECT_DETECTION_MODULE,
  FACIAL_RECOGNITION_MODULE,
  OCR_DOCUMENT_PROCESSING_MODULE,
  VIDEO_ANALYSIS_MODULE
} from './modules/computer-vision-ai';

// Import new expanded AI modules
import { REINFORCEMENT_LEARNING_FUNDAMENTALS_MODULES } from './modules/reinforcement-learning-fundamentals';
import { MULTI_AGENT_RL_MODULES } from './modules/multi-agent-rl';
import { ROBOTICS_RL_MODULES } from './modules/robotics-rl';
import { ADVANCED_RL_ALGORITHMS_MODULES } from './modules/advanced-rl-algorithms';
import { IOT_EDGE_AI_FUNDAMENTALS_MODULES } from './modules/iot-edge-ai-fundamentals';
import { EDGE_AI_OPTIMIZATION_MODULES } from './modules/edge-ai-optimization';
import { SMART_IOT_SYSTEMS_MODULES } from './modules/smart-iot-systems';
import { AI_ETHICS_FUNDAMENTALS_MODULES } from './modules/ai-ethics-fundamentals';
import { AI_BIAS_FAIRNESS_MODULES } from './modules/ai-bias-fairness';

// Import individual modules from the new specialized courses
import {
  DATA_PIPELINE_MLOPS_MODULE,
  ADVANCED_MLOPS_MODULE
} from './modules/data-pipeline-mlops';
import {
  QUANTUM_ML_FUNDAMENTALS_MODULE,
  QUANTUM_ALGORITHMS_MODULE
} from './modules/quantum-ml-fundamentals';
import {
  BLOCKCHAIN_AI_INTEGRATION_MODULE,
  DECENTRALIZED_AI_GOVERNANCE_MODULE
} from './modules/blockchain-ai-integration';

export const AI_COURSES: AICourse[] = [
  {
    id: 'ai-fundamentals',
    title: 'AI & Machine Learning Fundamentals',
    description: 'Comprehensive introduction to artificial intelligence and machine learning concepts, algorithms, and applications.',
    duration: '10 hours',
    level: 'beginner',
    estimatedHours: 10,
    category: 'Machine Learning',
    tags: ['AI', 'Machine Learning', 'Fundamentals', 'Python'],
    prerequisites: ['Basic programming knowledge', 'High school mathematics'],
    learningOutcomes: [
      'Understand core AI and ML concepts',
      'Implement basic machine learning algorithms',
      'Work with neural networks and deep learning',
      'Apply NLP techniques to real-world problems',
      'Evaluate and deploy ML models'
    ],
    modules: [
      ENHANCED_AI_FUNDAMENTALS_MODULE_1,
      ENHANCED_AI_FUNDAMENTALS_MODULE_2,
      ENHANCED_AI_FUNDAMENTALS_MODULE_3
    ]
  },
  {
    id: 'financial-ai',
    title: 'Financial AI & FinTech',
    description: 'Apply artificial intelligence and machine learning techniques to financial services, trading, and risk management.',
    duration: '25 hours',
    level: 'advanced',
    estimatedHours: 25,
    category: 'Domain Expertise',
    tags: ['AI', 'Finance', 'Trading', 'Risk Management', 'FinTech'],
    prerequisites: ['Basic ML knowledge', 'Financial markets understanding', 'Python programming'],
    learningOutcomes: [
      'Build algorithmic trading systems',
      'Implement risk management models',
      'Create fraud detection systems',
      'Develop credit scoring algorithms',
      'Apply AI to financial data analysis'
    ],
    modules: [
      FINANCIAL_AI_MODULE_1_FOUNDATIONS,
      FINANCIAL_AI_MODULE_2_ALGORITHMIC_TRADING,
      FINANCIAL_AI_MODULE_3_RISK_MANAGEMENT
    ]
  },
  {
    id: 'healthcare-ai',
    title: 'Healthcare AI & Medical Intelligence',
    description: 'Explore AI applications in healthcare, medical imaging, clinical decision support, and personalized medicine.',
    duration: '30 hours',
    level: 'advanced',
    estimatedHours: 30,
    category: 'Domain Expertise',
    tags: ['AI', 'Healthcare', 'Medical', 'Imaging', 'Clinical'],
    prerequisites: ['ML fundamentals', 'Basic medical knowledge', 'Python/R programming'],
    learningOutcomes: [
      'Analyze medical imaging with deep learning',
      'Build clinical decision support systems',
      'Implement drug discovery algorithms',
      'Create personalized medicine models',
      'Understand healthcare AI ethics and regulations'
    ],
    modules: [
      HEALTHCARE_AI_MODULE_1_FOUNDATIONS,
      HEALTHCARE_AI_MODULE_2_MEDICAL_IMAGING,
      HEALTHCARE_AI_MODULE_3
    ]
  },
  {
    id: 'gaming-ai',
    title: 'Gaming AI & Interactive Entertainment',
    description: 'Learn to create intelligent game characters, adaptive gameplay systems, and AI-driven game mechanics.',
    duration: '20 hours',
    level: 'intermediate',
    estimatedHours: 20,
    category: 'Domain Expertise',
    tags: ['AI', 'Gaming', 'NPCs', 'Procedural Generation', 'Game Development'],
    prerequisites: ['Programming experience', 'Basic AI concepts', 'Game development basics'],
    learningOutcomes: [
      'Implement intelligent NPC behavior',
      'Create adaptive difficulty systems',
      'Build procedural content generation',
      'Develop player analytics systems',
      'Apply reinforcement learning to games'
    ],
    modules: [
      GAMING_AI_MODULE_1,
      GAMING_AI_MODULE_2,
      GAMING_AI_MODULE_3,
      GAMING_AI_MODULE_4,
      GAMING_AI_MODULE_5,
      GAMING_AI_MODULE_6
    ]
  },
  {
    id: 'autonomous-systems',
    title: 'Autonomous Systems & Robotics AI',
    description: 'Build intelligent autonomous systems including self-driving vehicles, drones, and robotic systems.',
    duration: '35 hours',
    level: 'advanced',
    estimatedHours: 35,
    category: 'Domain Expertise',
    tags: ['AI', 'Robotics', 'Autonomous Vehicles', 'Computer Vision', 'Control Systems'],
    prerequisites: ['Strong programming skills', 'ML/DL knowledge', 'Control systems basics'],
    learningOutcomes: [
      'Design autonomous vehicle systems',
      'Implement robot perception and navigation',
      'Build intelligent control systems',
      'Create multi-agent coordination',
      'Apply safety and reliability principles'
    ],
    modules: [
      AUTONOMOUS_SYSTEMS_MODULE_1,
      AUTONOMOUS_SYSTEMS_MODULE_2,
      AUTONOMOUS_SYSTEMS_MODULE_3
    ]
  },
  {
    id: 'mlops-deployment',
    title: 'MLOps & Production AI Systems',
    description: 'Learn to deploy, monitor, and maintain machine learning systems in production environments.',
    duration: '15 hours',
    level: 'intermediate',
    estimatedHours: 15,
    category: 'MLOps',
    tags: ['MLOps', 'Deployment', 'Production', 'Monitoring', 'CI/CD'],
    prerequisites: ['ML experience', 'Software engineering basics', 'Cloud computing familiarity'],
    learningOutcomes: [
      'Deploy ML models to production',
      'Implement CI/CD for ML systems',
      'Monitor model performance and drift',
      'Scale ML systems efficiently',
      'Ensure ML system reliability and security'
    ],
    modules: [
      MLOPS_MODULE_1,
      MLOPS_MODULE_2
    ]
  },
  {
    id: 'genai-rag-supabase',
    title: 'Generative AI with RAG on Supabase',
    description: 'Build end-to-end RAG applications using Supabase for storage, vectors, and APIs with production best practices.',
    duration: '12 hours',
    level: 'intermediate',
    estimatedHours: 12,
    category: 'Generative AI',
    tags: ['AI', 'RAG', 'Supabase', 'Embeddings'],
    prerequisites: ['Basic ML knowledge', 'TypeScript/Node basics'],
    learningOutcomes: [
      'Implement embeddings and vector search on Supabase',
      'Build retrieval pipelines and prompt construction',
      'Ship a production-ready RAG API',
    ],
    modules: [
      RAG_SUPABASE_MODULE_1
    ]
  },
  {
    id: 'ai-agent-development',
    title: 'AI Agent Development Masterclass',
    description: 'Learn to build autonomous AI agents using modern frameworks like LangChain, CrewAI, and custom architectures. Master multi-agent systems, tool integration, and advanced reasoning patterns.',
    level: 'advanced',
    duration: '25-30 hours',
    estimatedHours: 27,
    modules: [
      AI_AGENT_INTRO_MODULE,
      LANGCHAIN_ORCHESTRATION_MODULE,
      MULTI_AGENT_SYSTEMS_MODULE,
      TOOL_USING_AGENTS_MODULE,
      AGENT_MEMORY_MODULE,
      CONVERSATIONAL_AI_MODULE,
      ADVANCED_AGENT_PATTERNS_MODULE
    ],
    tags: ['AI Agents', 'LangChain', 'AutoGPT', 'Multi-Agent Systems', 'Tool Integration', 'Conversational AI'],
    prerequisites: ['Python programming', 'Basic AI/ML knowledge', 'API integration experience'],
    learningOutcomes: [
      'Build autonomous AI agents from scratch',
      'Master LangChain framework for agent orchestration',
      'Implement multi-agent collaboration systems',
      'Create tool-using agents with API integrations',
      'Design sophisticated memory and state management',
      'Deploy conversational AI with voice capabilities',
      'Implement advanced patterns like AutoGPT and ReAct'
    ],
    category: 'Advanced AI Development'
  },
  {
    id: 'ai-prototyping',
    title: 'AI Prototyping & Rapid Development',
    description: 'Rapidly prototype and deploy AI applications using Streamlit, Gradio, Hugging Face, and no-code platforms. Perfect for quickly validating AI concepts and building demos.',
    level: 'intermediate',
    duration: '18-22 hours',
    estimatedHours: 20,
    modules: [
      STREAMLIT_AI_MODULE,
      GRADIO_ML_MODULE,
      HUGGINGFACE_INTEGRATION_MODULE,
      NOCODE_AI_TOOLS_MODULE,
      AI_API_INTEGRATION_MODULE,
      VOICE_AI_DEVELOPMENT_MODULE
    ],
    tags: ['Streamlit', 'Gradio', 'Hugging Face', 'No-Code AI', 'Rapid Prototyping', 'Voice AI'],
    prerequisites: ['Python basics', 'Basic ML concepts'],
    learningOutcomes: [
      'Build interactive AI apps with Streamlit',
      'Create stunning ML demos with Gradio',
      'Deploy models using Hugging Face ecosystem',
      'Leverage no-code platforms for AI development',
      'Integrate multiple AI APIs and services',
      'Develop voice-enabled AI applications',
      'Rapidly validate and iterate on AI concepts'
    ],
    category: 'AI Prototyping'
  },
  {
    id: 'advanced-llm-integration',
    title: 'Advanced LLM Integration & Applications',
    description: 'Master enterprise-grade LLM integration with OpenAI API, custom models, function calling, advanced prompting, and production deployment strategies.',
    level: 'advanced',
    duration: '20-25 hours',
    estimatedHours: 22,
    modules: [
      CUSTOM_LLM_INTEGRATION_MODULE,
      FUNCTION_CALLING_TOOLS_MODULE,
      ADVANCED_PROMPT_ENGINEERING_MODULE,
      LLM_EVALUATION_MODULE,
      ENTERPRISE_LLM_DEPLOYMENT_MODULE
    ],
    tags: ['OpenAI API', 'Custom LLMs', 'Function Calling', 'Prompt Engineering', 'Enterprise AI', 'Model Evaluation'],
    prerequisites: ['Python proficiency', 'API development experience', 'Cloud platforms knowledge'],
    learningOutcomes: [
      'Master OpenAI API and fine-tuning workflows',
      'Integrate custom and open-source LLMs',
      'Implement sophisticated function calling systems',
      'Design advanced prompting strategies',
      'Build comprehensive evaluation frameworks',
      'Deploy LLMs securely in enterprise environments',
      'Optimize cost and performance at scale'
    ],
    category: 'Enterprise LLM'
  },
  {
    id: 'computer-vision-ai',
    title: 'Computer Vision AI Applications',
    description: 'Build production-ready computer vision systems including object detection, facial recognition, OCR, and video analysis using PyTorch and TensorFlow.',
    level: 'intermediate',
    duration: '16-20 hours',
    estimatedHours: 18,
    modules: [
      MODERN_COMPUTER_VISION_MODULE,
      REALTIME_OBJECT_DETECTION_MODULE,
      FACIAL_RECOGNITION_MODULE,
      OCR_DOCUMENT_PROCESSING_MODULE,
      VIDEO_ANALYSIS_MODULE
    ],
    tags: ['Computer Vision', 'Object Detection', 'Facial Recognition', 'OCR', 'Video Analysis', 'PyTorch', 'TensorFlow'],
    prerequisites: ['Python programming', 'Basic deep learning', 'Linear algebra fundamentals'],
    learningOutcomes: [
      'Build modern computer vision models with PyTorch/TensorFlow',
      'Implement real-time object detection and tracking',
      'Create secure facial recognition systems',
      'Develop intelligent document processing with OCR',
      'Build video analysis and action recognition systems',
      'Deploy CV models for production use',
      'Optimize performance for edge and mobile devices'
    ],
    category: 'Computer Vision'
  },
  // New Reinforcement Learning Courses
  {
    id: 'reinforcement-learning-fundamentals',
    title: 'Reinforcement Learning Fundamentals',
    description: 'Master the fundamentals of reinforcement learning with hands-on implementation of Q-learning, policy gradients, and deep RL.',
    level: 'intermediate',
    duration: '15-18 hours',
    estimatedHours: 16,
    modules: REINFORCEMENT_LEARNING_FUNDAMENTALS_MODULES,
    tags: ['Reinforcement Learning', 'Q-Learning', 'Policy Gradients', 'Deep RL', 'Game AI'],
    prerequisites: ['Python programming', 'Basic machine learning', 'Linear algebra'],
    learningOutcomes: [
      'Understand RL fundamentals and Markov Decision Processes',
      'Implement Q-learning and policy gradient algorithms',
      'Build deep reinforcement learning agents',
      'Apply RL to games and control problems'
    ],
    category: 'Reinforcement Learning'
  },
  {
    id: 'multi-agent-rl',
    title: 'Multi-Agent Reinforcement Learning',
    description: 'Explore multi-agent systems, cooperation, competition, and emergent behaviors in complex environments.',
    level: 'advanced',
    duration: '12-15 hours',
    estimatedHours: 13,
    modules: MULTI_AGENT_RL_MODULES,
    tags: ['Multi-Agent Systems', 'Game Theory', 'Cooperation', 'Competition', 'Emergent Behavior'],
    prerequisites: ['Reinforcement learning basics', 'Game theory knowledge'],
    learningOutcomes: [
      'Design multi-agent RL systems',
      'Implement cooperation and competition strategies',
      'Handle emergent behaviors in complex environments'
    ],
    category: 'Reinforcement Learning'
  },
  {
    id: 'robotics-rl-applications',
    title: 'Robotics RL Applications',
    description: 'Apply reinforcement learning to robotics control, manipulation, and autonomous systems.',
    level: 'advanced',
    duration: '18-22 hours',
    estimatedHours: 20,
    modules: [...ROBOTICS_RL_MODULES, ...ADVANCED_RL_ALGORITHMS_MODULES],
    tags: ['Robotics', 'Control Systems', 'Manipulation', 'Autonomous Systems', 'Sim-to-Real'],
    prerequisites: ['Strong RL knowledge', 'Control systems basics', 'Robotics fundamentals'],
    learningOutcomes: [
      'Apply RL to robotic control and manipulation',
      'Implement advanced RL algorithms like SAC and TD3',
      'Handle sim-to-real transfer challenges'
    ],
    category: 'Reinforcement Learning'
  },
  // New IoT & Edge AI Courses
  {
    id: 'iot-edge-ai-fundamentals',
    title: 'IoT & Edge AI Fundamentals',
    description: 'Build intelligent IoT systems with edge computing, model optimization, and distributed intelligence.',
    level: 'intermediate',
    duration: '14-17 hours',
    estimatedHours: 15,
    modules: [...IOT_EDGE_AI_FUNDAMENTALS_MODULES, ...EDGE_AI_OPTIMIZATION_MODULES],
    tags: ['IoT', 'Edge Computing', 'Model Optimization', 'Quantization', 'Pruning'],
    prerequisites: ['Python programming', 'Basic ML knowledge', 'Embedded systems basics'],
    learningOutcomes: [
      'Design edge-optimized AI models',
      'Implement model compression techniques',
      'Deploy AI on resource-constrained devices'
    ],
    category: 'Edge AI'
  },
  {
    id: 'smart-iot-systems',
    title: 'Smart IoT Systems Development',
    description: 'Create intelligent IoT ecosystems with sensor fusion, federated learning, and distributed AI.',
    level: 'advanced',
    duration: '16-20 hours',
    estimatedHours: 18,
    modules: SMART_IOT_SYSTEMS_MODULES,
    tags: ['Smart IoT', 'Sensor Fusion', 'Federated Learning', 'Distributed AI', 'Smart Cities'],
    prerequisites: ['IoT fundamentals', 'Distributed systems', 'AI/ML experience'],
    learningOutcomes: [
      'Build smart IoT systems with AI',
      'Implement sensor fusion and federated learning',
      'Design scalable distributed AI architectures'
    ],
    category: 'Edge AI'
  },
  // New AI Ethics Courses
  {
    id: 'ai-ethics-fundamentals',
    title: 'AI Ethics & Responsible AI',
    description: 'Understand ethical implications of AI and build responsible, fair, and transparent AI systems.',
    level: 'intermediate',
    duration: '12-15 hours',
    estimatedHours: 13,
    modules: [...AI_ETHICS_FUNDAMENTALS_MODULES, ...AI_BIAS_FAIRNESS_MODULES],
    tags: ['AI Ethics', 'Responsible AI', 'Fairness', 'Bias Mitigation', 'Transparency'],
    prerequisites: ['Basic AI/ML knowledge', 'Understanding of social implications'],
    learningOutcomes: [
      'Understand AI ethics principles and frameworks',
      'Identify and mitigate bias in AI systems',
      'Implement fair and transparent AI solutions'
    ],
    category: 'AI Ethics'
  },
  // Reinforcement Learning & Robotics Courses  
  {
    id: 'reinforcement-learning-robotics',
    title: 'Reinforcement Learning for Robotics',
    description: 'Apply RL algorithms to robotic systems and autonomous agents.',
    level: 'advanced',
    duration: '8-10 hours',
    estimatedHours: 9,
    modules: [...ROBOTICS_RL_MODULES, ...ADVANCED_RL_ALGORITHMS_MODULES],
    tags: ['reinforcement-learning', 'robotics', 'autonomous-systems', 'control-theory'],
    prerequisites: ['Machine Learning Fundamentals', 'Python Programming'],
    learningOutcomes: [
      'Implement RL algorithms for robotic control',
      'Design reward functions for robotic tasks',
      'Build autonomous navigation systems',
      'Apply deep RL to complex robotic scenarios'
    ],
    category: 'Reinforcement Learning'
  },
  // MLOps & Engineering Courses
  {
    id: 'data-pipeline-mlops',
    title: 'Data Pipeline & MLOps',
    description: 'Build scalable data pipelines and implement MLOps practices.',
    level: 'intermediate',
    duration: '8-10 hours',
    estimatedHours: 9,
    modules: [DATA_PIPELINE_MLOPS_MODULE, ADVANCED_MLOPS_MODULE],
    tags: ['mlops', 'data-engineering', 'deployment', 'monitoring'],
    prerequisites: ['Machine Learning Fundamentals', 'Python Programming'],
    learningOutcomes: [
      'Design scalable data pipelines',
      'Implement CI/CD for ML models',
      'Set up model monitoring and observability',
      'Deploy ML models in production'
    ],
    category: 'MLOps & Engineering'
  },
  // Quantum Computing & AI Courses
  {
    id: 'quantum-machine-learning',
    title: 'Quantum Machine Learning',
    description: 'Explore quantum computing applications in machine learning.',
    level: 'advanced',
    duration: '8-10 hours',
    estimatedHours: 9,
    modules: [QUANTUM_ML_FUNDAMENTALS_MODULE, QUANTUM_ALGORITHMS_MODULE],
    tags: ['quantum-computing', 'qml', 'algorithms', 'optimization'],
    prerequisites: ['Machine Learning Fundamentals', 'Linear Algebra'],
    learningOutcomes: [
      'Understand quantum computing principles',
      'Implement quantum machine learning algorithms',
      'Build quantum neural networks',
      'Apply quantum optimization techniques'
    ],
    category: 'Advanced Algorithms'
  },
  // Blockchain & Web3 AI Courses
  {
    id: 'blockchain-ai-integration',
    title: 'Blockchain & AI Integration',
    description: 'Integrate blockchain technology with AI systems and create decentralized AI.',
    level: 'intermediate',
    duration: '8-10 hours',
    estimatedHours: 9,
    modules: [BLOCKCHAIN_AI_INTEGRATION_MODULE, DECENTRALIZED_AI_GOVERNANCE_MODULE],
    tags: ['blockchain', 'dao', 'decentralized-ai', 'smart-contracts'],
    prerequisites: ['Machine Learning Fundamentals', 'Blockchain Basics'],
    learningOutcomes: [
      'Build decentralized AI systems',
      'Implement smart contracts for ML governance',
      'Create tokenized AI models',
      'Design DAO governance for AI'
    ],
    category: 'Blockchain & Web3'
  }
];

// Development-only validation
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log(`🎓 AI Course System Loaded: ${AI_COURSES.length} courses across ${new Set(AI_COURSES.map(c => c.category)).size} categories`);
}
